/**
 * Shared report-notes utilities.
 *
 * Reports surface notes inline next to each noteable entity column. The
 * primitives below let each report wire that up with minimal local code:
 *
 *   - `REPORT_NOTES_FIELD_NAMES`: the set of field names this module knows
 *     how to render. Reports use this to detect notes columns when iterating
 *     `tableFields` (e.g. when formatting for CSV export).
 *
 *   - `buildReportNotesScope(fieldName, row)`: derives the NotesPanel scope
 *     for the modal opened from a cell click. Each known field knows which
 *     row property holds its entity's UUID.
 *
 *   - `makeNotesCellRenderer(...)`: factory for the `cellRenderer` prop on
 *     `TableMultiSelection` fields. Renders an icon + count badge; click
 *     fires the supplied opener with (title, scope).
 *
 *   - `formatNotesForCsv(notes)`: formats an array of `ReportNoteRow` as
 *     newline-separated readable text for CSV export. Uses `formatDateTime`
 *     so dates render consistently with the rest of the app.
 *
 *   - `ReportNotesModal`: drop-in modal wrapping NotesPanel in read-only
 *     mode. All reports use this so the look matches every other notes view.
 */
import { useTranslation } from "react-i18next";
import { ChatBubbleLeftEllipsisIcon } from "@heroicons/react/24/outline";

import Modal from "@components/layout/Modal";
import NotesPanel, { NotesPanelProps } from "@components/notes/NotesPanel";
import { DBTables } from "@utils/constants/dbTables";
import { formatDateTime } from "@utils/formatDateTime";

/** Slim note row returned by the backend for report cells. */
export type ReportNoteRow = {
  body: string;
  authorName: string;
  createdAt: string;
};

type ScopeBuilder = (row: Record<string, any>) => NotesPanelProps | null;

/**
 * Per field name → how to derive a NotesPanel scope from the row. Falls back
 * to checking both plain and `_` prefixed ID keys so it works for reports
 * that expose IDs as first-class fields (Water Taken GW/SW, …) and for the
 * General report which uses `_*` internal IDs.
 */
const pick = (row: Record<string, any>, ...keys: string[]): string | undefined =>
  keys.map(k => row[k]).find(v => typeof v === "string" && v.length > 0);

const SCOPE_BUILDERS: Record<string, ScopeBuilder> = {
  subscriberNotes: row => {
    const id = pick(row, "subscriberId", "_subscriberId");
    return id ? { subscriberId: id } : null;
  },
  schemeNotes: row => {
    const id = pick(row, "level1ResourceId", "_level1ResourceId");
    return id
      ? { referenceTable: DBTables.WRSHierarchies, referenceId: id }
      : null;
  },
  zoneNotes: row => {
    const id = pick(row, "level0ResourceId", "_level0ResourceId");
    return id
      ? { referenceTable: DBTables.Level0WRS, referenceId: id }
      : null;
  },
  priorityNotes: row => {
    const id = pick(row, "waterClassId", "_waterClassId");
    return id
      ? { referenceTable: DBTables.WaterClasses, referenceId: id }
      : null;
  },
  entitlementNotes: row => {
    const id = pick(row, "extractionRightId", "_extractionRightId");
    return id
      ? { referenceTable: DBTables.ExtractionRights, referenceId: id }
      : null;
  },
  offtakeNotes: row => {
    const id = pick(row, "extractionPointId", "_extractionPointId");
    return id
      ? { referenceTable: DBTables.ExtractionPoints, referenceId: id }
      : null;
  },
  meterNotes: row => {
    const id = pick(row, "meterId", "_meterId");
    return id ? { referenceTable: DBTables.Meters, referenceId: id } : null;
  },
  billingGroupNotes: row => {
    const id = pick(row, "billingGroupId", "_billingGroupId");
    return id
      ? { referenceTable: DBTables.BillingGroups, referenceId: id }
      : null;
  },
  waterChargeNotes: row => {
    const id = pick(row, "waterChargeId", "_waterChargeId");
    return id
      ? { referenceTable: DBTables.WaterCharges, referenceId: id }
      : null;
  },
  // Reports that surface administrative approvals (transfers) directly —
  // SeasonalAssign, Permanent Trade, etc.
  transferNotes: row => {
    const id = pick(row, "administrativeApprovalId");
    return id
      ? { referenceTable: DBTables.AdministrativeApproval, referenceId: id }
      : null;
  },
  // SeasonalAssign + Permanent Trade have two subscribers per row (seller +
  // buyer) and two zones (from + to). Per-side field names so each can
  // resolve to its own scope.
  sellerNotes: row => {
    const id = pick(row, "sellerId");
    return id ? { subscriberId: id } : null;
  },
  buyerNotes: row => {
    const id = pick(row, "buyerId");
    return id ? { subscriberId: id } : null;
  },
  zoneFromNotes: row => {
    const id = pick(row, "level0ResourceIdFrom");
    return id
      ? { referenceTable: DBTables.Level0WRS, referenceId: id }
      : null;
  },
  zoneToNotes: row => {
    const id = pick(row, "level0ResourceIdTo");
    return id
      ? { referenceTable: DBTables.Level0WRS, referenceId: id }
      : null;
  },
};

export const REPORT_NOTES_FIELD_NAMES = new Set(Object.keys(SCOPE_BUILDERS));

/**
 * True when a report row carries at least one note in any of its notes columns.
 * Each notes field holds a `ReportNoteRow[]` (empty when the row has none), so a
 * row "has notes" if any known notes field is a non-empty array. Powers the
 * "show only rows with notes" filter on report filter bars.
 */
export const reportRowHasNotes = (row: Record<string, any>): boolean =>
  Array.from(REPORT_NOTES_FIELD_NAMES).some(
    fieldName => Array.isArray(row[fieldName]) && row[fieldName].length > 0,
  );

export const buildReportNotesScope = (
  fieldName: string,
  row: Record<string, any>,
): NotesPanelProps | null => {
  const builder = SCOPE_BUILDERS[fieldName];
  return builder ? builder(row) : null;
};

/** Format `ReportNoteRow[]` → readable multiline string for CSV. */
export const formatNotesForCsv = (notes: ReportNoteRow[]): string =>
  notes
    .map(
      n =>
        `[${formatDateTime(new Date(n.createdAt))}] ${n.authorName || "—"}: ${n.body}`,
    )
    .join("\n");

/**
 * Factory for a `TableMultiSelection` field-level `cellRenderer`. The renderer
 * is a no-op when the value isn't an array or the row doesn't have the right
 * scope key. When it can render, it shows an icon + count and clicks open the
 * supplied opener with (title, scope) so the parent can mount the modal.
 */
export const makeNotesCellRenderer = (
  fieldLabel: string,
  fieldName: string,
  onOpen: (title: string, scope: NotesPanelProps) => void,
  titleForCount: (count: number) => string,
) =>
  (value: any, row: Record<string, any>) => {
    if (!Array.isArray(value) || value.length === 0) return null;
    const scope = buildReportNotesScope(fieldName, row);
    if (!scope) return null;
    return (
      <button
        type="button"
        onClick={() => onOpen(fieldLabel, scope)}
        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        title={titleForCount(value.length)}
      >
        <ChatBubbleLeftEllipsisIcon className="h-4 w-4" aria-hidden="true" />
        <span className="text-xs font-medium">{value.length}</span>
      </button>
    );
  };

/**
 * Renders `NotesPanel` from a `NotesPanelProps` value in a way TypeScript can
 * verify against the discriminated union. Spreading a union into JSX collapses
 * to the intersection, which the panel's mutually-exclusive `never` fields
 * reject — so we branch on which scope discriminant is set and pass the
 * appropriate prop set. Mirrors the truthy-check pattern in NotesPanel's own
 * composer-scope resolver (`"subscriberId" in props && props.subscriberId`)
 * which narrows past the `?: never` optional-undefined edge.
 */
const renderReadOnlyPanel = (scope: NotesPanelProps) => {
  if ("subscriberId" in scope && scope.subscriberId) {
    return (
      <NotesPanel
        subscriberId={scope.subscriberId}
        artifactName={scope.artifactName}
        readOnly
        reportableOnly
      />
    );
  }
  if ("level1ResourceId" in scope && scope.level1ResourceId) {
    return (
      <NotesPanel
        level1ResourceId={scope.level1ResourceId}
        artifactName={scope.artifactName}
        readOnly
        reportableOnly
      />
    );
  }
  if ("referenceTable" in scope && scope.referenceTable && scope.referenceId) {
    return (
      <NotesPanel
        referenceTable={scope.referenceTable}
        referenceId={scope.referenceId}
        artifactName={scope.artifactName}
        readOnly
        reportableOnly
      />
    );
  }
  return null;
};

type ReportNotesModalProps = {
  open: boolean;
  title: string;
  scope: NotesPanelProps | null;
  onClose: () => void;
};

/**
 * Modal frame around a read-only NotesPanel. Reused by every report so the
 * look matches RowNotesModal and the entity audit-trail Notes tab — just
 * without composer / Quote / Archive / Reportable-toggle / filter chrome.
 */
export const ReportNotesModal: React.FunctionComponent<ReportNotesModalProps> = ({
  open,
  title,
  scope,
  onClose,
}) => {
  const { t } = useTranslation();
  return (
    <Modal open={open} className="sm:max-w-3xl">
      <div className="flex flex-col">
        <header className="border-b border-gray-200 p-4">
          <h3 className="text-lg font-bold leading-6 text-gray-900">{title}</h3>
        </header>
        <div className="max-h-[70vh] overflow-y-auto">
          {scope && renderReadOnlyPanel(scope)}
        </div>
        <footer className="border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="btn-primary text-sm rounded"
          >
            {t("common.close")}
          </button>
        </footer>
      </div>
    </Modal>
  );
};
